import * as Sentry from '@sentry/react';
import Swal from 'sweetalert2';
import {isCancel} from 'axios';
import {AppConfig, CustomerModel, FormFieldProps, OperationModel, ServiceProvider} from '../../types';
import i18n from '../i18n';
import icons from '../utils/icons.ts';

export function random(length = 40) {
  let text = '';
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < length; i += 1) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

export function debounce<F extends (...args: any) => void>(
  func: F,
  wait: number,
  immediate: boolean = false
): ((...args: Parameters<F>) => void) & { stop: () => void } {
  let timeout: any = null;
  const _debounce = (...args: any[]) => {
    // @ts-ignore
    const context = this;
    const later = function __debounce() {
      timeout = null;
      if (!immediate) {
        func.apply(context, args);
      }
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) {
      func.apply(context, args);
    }
  };
  _debounce.stop = () => clearTimeout(timeout);
  return _debounce;
}

export const camelize = (str: string) => {
  return str.toLowerCase().replace(/^(.)/, (match, chr) => chr.toUpperCase());
};

export const extract = (values: Record<string, any>, name: string) => {
  return name.split('.').reduce((acc, item) => acc && acc[item], values);
};

export const flattenObject = (
  value: Record<string, never>,
  prefix = '',
  result: Record<string, never> = {}
) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return result;
    }

    if (typeof value[0] !== 'object') {
      // @ts-ignore
      result[prefix] = value[0];
      return result;
    }

    value.forEach((item, index) => {
      const arrayKey = prefix ? `${prefix}[${index}]` : String(index);

      if (item !== null && typeof item === 'object') {
        flattenObject(item, arrayKey, result);
      } else {
        // @ts-ignore
        result[arrayKey] = item;
      }
    });

    return result;
  }

  if (value !== null && typeof value === 'object') {
    Object.entries(value).forEach(([key, item]) => {
      const dotKey = prefix ? `${prefix}.${key}` : key;

      if (item !== null && typeof item === 'object') {
        flattenObject(item, dotKey, result);
      } else {
        result[dotKey] = item;
      }
    });

    return result;
  }

  if (prefix) {
    result[prefix] = value;
  }

  return result;
};

export const parseDjangoError = (json: any): Record<string, string> => {
  if (!json) {
    return {};
  }
  const { code } = json;
  delete json.code;

  if (typeof code === 'object') {
    return Object.keys(code).reduce(
      (acc, key) => ({ [key]: json[key].join('\n'), ...acc }),
      {}
    );
  }

  return Object.keys(json).reduce(
    (acc, key) => ({ [key]: Array.isArray(json[key]) ? json[key].join('\n') : json[key], ...acc }),
    {}
  );
};

export const processErrorResponse = (
  error: unknown,
  setErrors: (errors: Record<string, string>) => void = () => {},
  fallbackMessageKey = 'Default_failure_transaction_message'
) => {
  if (isCancel(error)) return;
  const raw = error && typeof error === 'object' && 'response' in error
    ? (error.response as { data?: unknown } | undefined)?.data : undefined;
  const response = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const parsedErrors = parseDjangoError({ ...response });
  setErrors(parsedErrors);
  const message = parsedErrors.non_field_errors ?? response.detail ?? response.message
    ?? i18n.t(fallbackMessageKey);
  Swal.fire({ title: i18n.t('Error'), text: String(message), icon: 'error',
    buttonsStyling: false, confirmButtonText: i18n.t('Ok_got_it'),
    customClass: { confirmButton: 'btn fw-bold btn-light-primary' } });
};

const buildOperationForm = (
  operation: OperationModel,
  inputs: Record<string, FormFieldProps<Record<string, any>>>,
  skips: string[]
) => {
  for (const param of operation.params) {
    let key = param.key;
    if (key == 'receiver') {
      key = 'b_party';
    }
    if (inputs[key]) {
      if (param.type == 'CHOICE') {
        inputs[key].options = (param.choices ?? []).map((c: { value: string; label: string }) => ({
          ...c,
          image: icons[c.value],
        }));
      } else if (param.type == 'MSISDN') {
        inputs[key].type = 'phone';
      }
      if (inputs[key].format == 'NUMBER') {
        inputs[key].type = 'number';
      }
      if (param.placeholders) {
        inputs[key].label = param.placeholders.en;
      }
    }

    skips.push(key);
  }
  for (const key of Object.keys(inputs)) {
    if (skips.includes(key)) {
      continue;
    }
    delete inputs[key];
  }
  return inputs;
};

export const buildServiceForm = (operation: OperationModel, configs: AppConfig) => {
  const fields: Record<string, FormFieldProps<Record<string, any>>> = {
    b_party: {
      name: 'b_party',
      label: i18n.t('Receiver Phone'),
      type: 'text',
      showCountryCode: false,
      placeholder: '670000000',
    },
    amount: {
      name: 'amount',
      label: i18n.t('Amount'),
      type: 'amount',
      prefix: 'FCFA ',
    },
    merchant: {
      name: 'merchant',
      type: 'select',
      label: i18n.t('Merchant'),
    },
    product: {
      name: 'product',
      type: 'megaOption',
      label: i18n.t('Product'),
      itemClassName: 'col-lg-4',
    },
    account: {
      name: 'account',
      type: 'select',
      label: i18n.t('Account'),
      options: [],
    },
  };

  // fields.account.options = configs.accounts.filter(a => a.provider === 'MESOMB').map(a => ({
  //   value: String(a.identifier),
  //   label: `${a.service_key} (${a.name})`,
  //   description: i18n.t('fees_rate', {rate: a.out_rate}),
  //   image: a.image
  // }))

  return buildOperationForm(operation, fields, [
    'provider',
    ...(['TVPurchase', 'BundlePurchase'].includes(operation.name)
      ? ['product']
      : []),
  ]);
};

export const countryFlagUrl = (code?: string): string => {
  return `https://flagsapi.com/${code?.toUpperCase()}/flat/64.png`;
};

export const getPaymentMethod = (
  country: string
): Pick<ServiceProvider, 'key' | 'service_name' | 'logo'>[] => {
  return {
    CM: [
      {
        key: 'MTN',
        service_name: 'Mobile Money',
        logo: '/media/providers/logo-momo.jpg',
      },
      {
        key: 'ORANGE',
        service_name: 'Orange Money',
        logo: '/media/providers/orange-money.jpg',
      },
    ],
  }[country]!;
};



export const buildConfigs = (data: Omit<AppConfig, "providers" | "payment_pricing"> & { providers: ServiceProvider[]; payment_pricing?: AppConfig["payment_pricing"] }): AppConfig => {
  return {
    ...data,
    payment_pricing: data.payment_pricing ?? {},
    providers: data.providers.reduce(
      (acc: Record<string, any>, provider: any) => {
        acc[provider.key] = provider;
        return acc;
      },
      {}
    ),
  };
};

export const urlToBase64 = async (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Handle CORS
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = img.width;
      canvas.height = img.height;

      ctx?.drawImage(img, 0, 0);

      const dataURL = canvas.toDataURL('image/jpeg', 0.8);
      resolve(dataURL);
    };
    img.onerror = reject;
    img.src = url;
  });
};

export function toStringCustomer(customer?: CustomerModel): string {
  if (!customer) {
    return '';
  }
  if (customer.first_name && customer.last_name) {
    return `${customer.first_name} ${customer.last_name}`;
  }
  if (customer.first_name) {
    return customer.first_name;
  }
  if (customer.last_name) {
    return customer.last_name;
  }
  return customer.phone || customer.email || '';
}
